import { SectionTitle } from "../_shared/section-title";
import { PageShell, Stack } from "../_shared/page-shell";
import { Card } from "../_shared/card";
import {
  IconCalendar,
  IconClock,
  IconHouse,
  IconPlus,
  IconWarning,
} from "../_shared/icons";
import { navigate } from "../_shared/router";
import { children, pastAppointments, type Appointment } from "../_data/mock";
import { useDemo } from "../_shared/demo.provider";
import { formatFrenchDate, formatFrenchDateShort } from "../_shared/date-fmt";

const childAccent = {
  orange: "border-l-[var(--child-1-primary)] bg-[var(--child-1-track)]",
  blue: "border-l-[var(--child-2-primary)] bg-[var(--child-2-track)]",
} as const;

const childPill = {
  orange: "bg-[var(--child-1-light)] text-[var(--child-1-primary)]",
  blue: "bg-[var(--child-2-light)] text-[var(--child-2-primary)]",
} as const;

function AppointmentRow({
  rdv,
  withActions,
  short,
  onCancel,
  onReschedule,
}: {
  rdv: Appointment;
  withActions?: boolean;
  short?: boolean;
  onCancel?: () => void;
  onReschedule?: () => void;
}) {
  const child = children.find((c) => c.id === rdv.childId);
  if (!child) return null;
  const date = short
    ? formatFrenchDateShort(rdv.isoDate)
    : formatFrenchDate(rdv.isoDate);
  return (
    <li className={`rounded-2xl border-l-4 ${childAccent[child.color]} p-3`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[14.5px] font-semibold text-[var(--text-1)]">
            {rdv.kind}
          </p>
          <p className="mt-0.5 flex items-center gap-2 text-[12.5px] text-[var(--text-2)]">
            <span
              className={`inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold ${childPill[child.color]}`}
            >
              {child.firstName}
            </span>
            <span className="first-letter:uppercase">{date}</span>
            <span aria-hidden>·</span>
            <span>{rdv.time}</span>
          </p>
        </div>
      </div>
      {withActions ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate("cabinet")}
            className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[var(--surface-1)] px-3 text-[12.5px] font-semibold text-[var(--text-2)] ring-1 ring-[var(--border-1)] transition hover:bg-[var(--surface-2)]"
          >
            <IconHouse width={14} height={14} /> Itinéraire
          </button>
          <button
            type="button"
            onClick={onReschedule}
            className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[var(--surface-1)] px-3 text-[12.5px] font-semibold text-[var(--text-2)] ring-1 ring-[var(--border-1)] transition hover:bg-[var(--surface-2)]"
          >
            <IconClock width={14} height={14} /> Déplacer
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[var(--surface-1)] px-3 text-[12.5px] font-semibold text-[var(--sf-primary)] ring-1 ring-[var(--sf-primary)]/30 transition hover:bg-[var(--sf-primary-100)]"
          >
            <IconWarning width={14} height={14} /> Annuler
          </button>
        </div>
      ) : null}
    </li>
  );
}

export function AppointmentsPage() {
  const { upcomingAppointments, cancelAppointment } = useDemo();

  return (
    <>
      <SectionTitle title="Rendez-vous" />
      <PageShell>
        <Stack>
          <button
            type="button"
            onClick={() => navigate("book")}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[var(--sf-primary)] px-5 text-[15px] font-semibold text-white shadow-sm transition active:scale-[0.99] sm:mx-auto sm:max-w-sm"
          >
            <IconPlus width={18} height={18} />
            Prendre un nouveau rendez-vous
          </button>

          <Card title="À venir" icon={<IconCalendar width={20} height={20} />}>
            {upcomingAppointments.length === 0 ? (
              <p className="text-[13px] text-[var(--text-2)]">
                Aucun rendez-vous prévu.
              </p>
            ) : (
              <ul className="space-y-2.5">
                {upcomingAppointments.map((rdv) => (
                  <AppointmentRow
                    key={rdv.id}
                    rdv={rdv}
                    withActions
                    onCancel={() =>
                      navigate("contact", { action: "cancel", id: rdv.id })
                    }
                    onReschedule={() =>
                      navigate("book", { reschedule: rdv.id })
                    }
                  />
                ))}
              </ul>
            )}
          </Card>

          <Card title="Historique" icon={<IconClock width={20} height={20} />}>
            <ul className="space-y-2.5">
              {pastAppointments.map((rdv) => (
                <AppointmentRow key={rdv.id} rdv={rdv} short />
              ))}
            </ul>
          </Card>
        </Stack>
      </PageShell>
    </>
  );
}
